/**
 * Ledger / finance API endpoints
 */

import { fetchApi, fetchRawApi } from '../core';
import { objectIdPath } from '../path';
import type {
  CourseBalancesResponse,
  CourseRosterBalancesResponse,
  CreateLedgerEntryData,
  CreatePaidChargeData,
  CreateFeeTypeData,
  CreatePaymentMethodData,
  FeeTypeResponse,
  FinanceSummaryParams,
  FinanceSummaryResponse,
  FinanceCourseDirectoryItem,
  FinanceCourseDirectoryParams,
  FinanceStudentDirectoryItem,
  FinanceStudentDirectoryParams,
  LedgerActivityParams,
  LedgerActivityResponse,
  LedgerEntryListResponse,
  LedgerEntryResponse,
  LedgerQueryParams,
  OutstandingBalancesResponse,
  PaidChargeResponse,
  PaymentMethodResponse,
  PriceRosterData,
  PriceRosterResponse,
  StudentBalanceResponse,
  UnpricedEnrollmentsResponse,
  UpdateFeeTypeData,
  UpdateLedgerEntryData,
  UpdatePaymentMethodData,
  VoidLedgerEntryData,
} from '../types';

function buildLedgerQuery(params: LedgerQueryParams = {}): string {
  const search = new URLSearchParams();
  if (params.student_id) search.set('student_id', params.student_id);
  if (params.course_id) search.set('course_id', params.course_id);
  // The API takes one comma-separated parameter; a single value is exactly
  // what it always was, so an old caller's URL is unchanged.
  if (params.entry_types?.length) search.set('entry_type', params.entry_types.join(','));
  if (params.status) search.set('status', params.status);
  if (params.payment_method_id) search.set('payment_method_id', params.payment_method_id);
  if (params.fee_type_id) search.set('fee_type_id', params.fee_type_id);
  if (params.category) search.set('category', params.category);
  if (typeof params.start_ms === 'number') search.set('start_ms', String(params.start_ms));
  if (typeof params.end_ms === 'number') search.set('end_ms', String(params.end_ms));
  if (typeof params.include_voided === 'boolean')
    search.set('include_voided', String(params.include_voided));
  if (params.without_course) search.set('without_course', 'true');
  if (typeof params.page === 'number') search.set('page', String(params.page));
  if (typeof params.limit === 'number') search.set('limit', String(params.limit));
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

export function listEntries(
  params: LedgerQueryParams = {},
  componentName: string = 'Unknown'
) {
  return fetchApi<LedgerEntryListResponse>(
    `/ledger${buildLedgerQuery(params)}`,
    {},
    componentName
  );
}

export function createEntry(
  data: CreateLedgerEntryData,
  componentName: string = 'Unknown'
) {
  return fetchApi<LedgerEntryResponse>(
    '/ledger',
    { method: 'POST', body: JSON.stringify(data) },
    componentName
  );
}

export function createPaidCharge(
  data: CreatePaidChargeData,
  componentName: string = 'Unknown'
) {
  return fetchApi<PaidChargeResponse>(
    '/ledger/paid-charges',
    { method: 'POST', body: JSON.stringify(data) },
    componentName
  );
}

export function getEntry(entryId: string, componentName: string = 'Unknown') {
  return fetchApi<LedgerEntryResponse>(
    `/ledger/${objectIdPath(entryId, 'ledger entry')}`,
    {},
    componentName,
  );
}

export function updateEntry(
  entryId: string,
  data: UpdateLedgerEntryData,
  componentName: string = 'Unknown'
) {
  return fetchApi<LedgerEntryResponse>(
    `/ledger/${objectIdPath(entryId, 'ledger entry')}`,
    { method: 'PUT', body: JSON.stringify(data) },
    componentName
  );
}

export function voidEntry(
  entryId: string,
  data: VoidLedgerEntryData,
  componentName: string = 'Unknown'
) {
  return fetchApi<LedgerEntryResponse>(
    `/ledger/${objectIdPath(entryId, 'ledger entry')}/void`,
    { method: 'POST', body: JSON.stringify(data) },
    componentName
  );
}

export function listPaymentMethods(
  includeInactive: boolean = false,
  componentName: string = 'Unknown'
) {
  const qs = includeInactive ? '?include_inactive=true' : '';
  return fetchApi<PaymentMethodResponse[]>(
    `/payment-methods${qs}`,
    {},
    componentName
  );
}

export function createPaymentMethod(
  data: CreatePaymentMethodData,
  componentName: string = 'Unknown'
) {
  return fetchApi<PaymentMethodResponse>(
    '/payment-methods',
    { method: 'POST', body: JSON.stringify(data) },
    componentName
  );
}

export function updatePaymentMethod(
  methodId: string,
  data: UpdatePaymentMethodData,
  componentName: string = 'Unknown'
) {
  return fetchApi<PaymentMethodResponse>(
    `/payment-methods/${methodId}`,
    { method: 'PUT', body: JSON.stringify(data) },
    componentName
  );
}

export function archivePaymentMethod(
  methodId: string,
  componentName: string = 'Unknown'
) {
  return fetchApi<PaymentMethodResponse>(
    `/payment-methods/${methodId}/archive`,
    { method: 'POST' },
    componentName
  );
}

export function listFeeTypes(
  includeInactive: boolean = false,
  componentName: string = 'Unknown'
) {
  const qs = includeInactive ? '?include_inactive=true' : '';
  return fetchApi<FeeTypeResponse[]>(`/fee-types${qs}`, {}, componentName);
}

export function createFeeType(
  data: CreateFeeTypeData,
  componentName: string = 'Unknown'
) {
  return fetchApi<FeeTypeResponse>(
    '/fee-types',
    { method: 'POST', body: JSON.stringify(data) },
    componentName
  );
}

export function updateFeeType(
  feeTypeId: string,
  data: UpdateFeeTypeData,
  componentName: string = 'Unknown'
) {
  return fetchApi<FeeTypeResponse>(
    `/fee-types/${objectIdPath(feeTypeId, 'fee type')}`,
    { method: 'PUT', body: JSON.stringify(data) },
    componentName
  );
}

export function archiveFeeType(
  feeTypeId: string,
  expectedUpdatedAt: number,
  componentName: string = 'Unknown'
) {
  return fetchApi<FeeTypeResponse>(
    `/fee-types/${objectIdPath(feeTypeId, 'fee type')}/archive`,
    { method: 'POST', body: JSON.stringify({ expected_updated_at: expectedUpdatedAt }) },
    componentName
  );
}

export function studentBalance(
  studentId: string,
  componentName: string = 'Unknown'
) {
  return fetchApi<StudentBalanceResponse>(
    `/students/${objectIdPath(studentId, 'student')}/balance`,
    {},
    componentName
  );
}

export function courseBalances(
  courseId: string,
  componentName: string = 'Unknown'
) {
  return fetchApi<CourseBalancesResponse>(
    `/courses/${objectIdPath(courseId, 'course')}/balances`,
    {},
    componentName
  );
}

/**
 * Every roster member's payment standing on one course.
 *
 * Unlike `courseBalances`, which is derived only from ledger entries, this
 * starts from the course roster — so a student nobody has charged is present
 * and marked `unbilled` rather than being absent entirely.
 *
 * Requires InstitutionAdmin or FinanceManager; answers 403 otherwise.
 */
export function courseRosterBalances(
  courseId: string,
  componentName: string = 'Unknown'
) {
  return fetchApi<CourseRosterBalancesResponse>(
    `/courses/${objectIdPath(courseId, 'course')}/roster-balances`,
    {},
    componentName
  );
}

export function ledgerActivity(
  params: LedgerActivityParams = {},
  componentName: string = 'Unknown'
) {
  const search = new URLSearchParams();
  if (typeof params.start_ms === 'number') search.set('start_ms', String(params.start_ms));
  if (typeof params.end_ms === 'number') search.set('end_ms', String(params.end_ms));
  if (params.entry_type) search.set('entry_type', params.entry_type);
  const qs = search.toString();
  return fetchApi<LedgerActivityResponse>(
    `/ledger/activity${qs ? `?${qs}` : ''}`,
    {},
    componentName
  );
}

export function financeSummary(
  params: FinanceSummaryParams = {},
  componentName: string = 'Unknown'
) {
  const search = new URLSearchParams();
  if (typeof params.start_ms === 'number') search.set('start_ms', String(params.start_ms));
  if (typeof params.end_ms === 'number') search.set('end_ms', String(params.end_ms));
  const qs = search.toString();
  return fetchApi<FinanceSummaryResponse>(
    `/ledger/summary${qs ? `?${qs}` : ''}`,
    {},
    componentName
  );
}

export function outstandingBalances(componentName: string = 'Unknown') {
  return fetchApi<OutstandingBalancesResponse>(
    '/ledger/outstanding',
    {},
    componentName
  );
}

export function financeStudents(
  params: FinanceStudentDirectoryParams = {},
  componentName: string = 'Unknown'
) {
  return fetchApi<FinanceStudentDirectoryItem[]>(
    '/ledger/directory/students/search',
    { method: 'POST', body: JSON.stringify(params) },
    componentName
  );
}

export function financeCourses(componentName: string = 'Unknown') {
  return fetchApi<FinanceCourseDirectoryItem[]>(
    '/ledger/directory/courses',
    {},
    componentName
  );
}

export function financeCoursesByIds(
  params: FinanceCourseDirectoryParams,
  componentName: string = 'Unknown'
) {
  return fetchApi<FinanceCourseDirectoryItem[]>(
    '/ledger/directory/courses/search',
    { method: 'POST', body: JSON.stringify(params) },
    componentName
  );
}

/** Enrollments carrying no tuition charge, grouped by course. */
export function unpricedEnrollments(componentName: string = 'Unknown') {
  return fetchApi<UnpricedEnrollmentsResponse>(
    '/ledger/unpriced-enrollments',
    {},
    componentName
  );
}

/** Charge every unbilled student on a course at one list price. Safe to
 * repeat: students who already carry a live charge are skipped. */
export function priceCourseRoster(
  courseId: string,
  data: PriceRosterData,
  componentName: string = 'Unknown'
) {
  return fetchApi<PriceRosterResponse>(
    `/courses/${objectIdPath(courseId, 'course')}/price-roster`,
    { method: 'POST', body: JSON.stringify(data) },
    componentName
  );
}

/**
 * Returns the raw CSV text from the export endpoint. The audit event is
 * recorded server-side as part of the request.
 */
export async function exportCsv(
  params: LedgerQueryParams = {},
  componentName: string = 'Unknown'
): Promise<string> {
  const response = await fetchRawApi(
    `/ledger/export${buildLedgerQuery(params)}`,
    { method: 'GET' },
    componentName
  );
  if (!response.ok) {
    let message = `Export failed (${response.status})`;
    try {
      const body = await response.json();
      if (body && typeof body.error === 'string') message = body.error;
    } catch {
      /* swallow */
    }
    throw new Error(`[${componentName}] ${message}`);
  }
  return response.text();
}
